import * as y from "yup";
import type { AudienceData } from "@common/interfaces/KYDMarketing";
import { GUIDED_EDIT_DEFAULT_TIMEZONE } from "@common/interfaces/adsManagerGuidedEdit";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import type { FacebookPage, InstagramAccount } from "../types";
import {
  hasMixedGuidedCreativeMediaTypes,
  MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
} from "../utils/validateCreativeMediaType";

dayjs.extend(utc);
dayjs.extend(timezone);

export const PRIMARY_TEXT_MAX = 1000;

type FlowValidationContext = {
  requireAudienceSection?: boolean;
  requireBudgetSection?: boolean;
  /** IANA zone for schedule wall times (start_at / end_at) */
  eventTimezone?: string;
};

const shouldRequireAudienceSection = (
  context: FlowValidationContext | undefined
) => context?.requireAudienceSection !== false;

const shouldRequireBudgetSection = (
  context: FlowValidationContext | undefined
) => context?.requireBudgetSection !== false;

/**
 * When Smart Targeting is off, the flow still needs some manual narrowing:
 * custom audiences, interests, geo, or non-default demographics.
 */
export function hasManualAudienceTargetingSignal(params: {
  custom_audiences?: readonly unknown[] | null;
  interests?: readonly unknown[] | null;
  countries?: readonly unknown[] | null;
  geo_markets?: readonly unknown[] | null;
  regions?: readonly unknown[] | null;
  cities?: readonly unknown[] | null;
  age_min?: number | null;
  age_max?: number | null;
  genders?: string | null;
}): boolean {
  if ((params.custom_audiences?.length ?? 0) > 0) return true;
  if ((params.interests?.length ?? 0) > 0) return true;
  if (
    (params.countries?.length ?? 0) > 0 ||
    (params.geo_markets?.length ?? 0) > 0 ||
    (params.regions?.length ?? 0) > 0 ||
    (params.cities?.length ?? 0) > 0
  ) {
    return true;
  }
  if (params.age_min != null && params.age_min !== 18) return true;
  if (params.age_max != null && params.age_max !== 65) return true;
  const g = params.genders;
  if (g != null && g !== "" && g !== "all") return true;
  return false;
}

function resolveCreativeSlotState(value: {
  squareMediaUrl?: string | null;
  squareMediaFileKey?: string | null;
  squareMediaType?: string | null;
  verticalMediaUrl?: string | null;
  verticalMediaFileKey?: string | null;
  verticalMediaType?: string | null;
}): {
  hasSquareAsset: boolean;
  hasSquareType: boolean;
  hasVerticalAsset: boolean;
  hasVerticalType: boolean;
} {
  return {
    hasSquareAsset: Boolean(value?.squareMediaUrl || value?.squareMediaFileKey),
    hasSquareType: Boolean(value?.squareMediaType),
    hasVerticalAsset: Boolean(
      value?.verticalMediaUrl || value?.verticalMediaFileKey
    ),
    hasVerticalType: Boolean(value?.verticalMediaType),
  };
}

const creativeSchema = y
  .object({
    name: y.string().optional(),
    squareMediaUrl: y.string().nullable().optional(),
    squareMediaFileKey: y.string().nullable().optional(),
    squareMediaType: y.string().oneOf(["image", "video"]).nullable().optional(),
    verticalMediaUrl: y.string().nullable().optional(),
    verticalMediaFileKey: y.string().nullable().optional(),
    verticalMediaType: y
      .string()
      .oneOf(["image", "video"])
      .nullable()
      .optional(),
    primaryText: y
      .string()
      .required("Primary text is required")
      .max(
        PRIMARY_TEXT_MAX,
        `Primary text must be at most ${PRIMARY_TEXT_MAX} characters`
      ),
    fbPage: y
      .mixed<FacebookPage | null>()
      .nullable()
      .test("fb-page-required", "Facebook Page is required", (value) =>
        Boolean(value?.id)
      ),
    instagramAccount: y
      .mixed<InstagramAccount | null>()
      .nullable()
      .optional(),
  })
  .test(
    "creative-at-least-one-media-slot",
    "At least one creative media format is required",
    (value) => {
      const {
        hasSquareAsset,
        hasSquareType,
        hasVerticalAsset,
        hasVerticalType,
      } = resolveCreativeSlotState(value ?? {});
      return (
        (hasSquareAsset && hasSquareType) ||
        (hasVerticalAsset && hasVerticalType)
      );
    }
  )
  .test(
    "creative-media-types-must-match",
    MATCHING_CREATIVE_MEDIA_TYPES_ERROR,
    (value) => !hasMixedGuidedCreativeMediaTypes(value)
  );

export const CreateCampaignFlowSchema = y.object({
  creatives: y
    .array()
    .of(creativeSchema)
    .min(1, "At least one creative is required")
    .test(
      "creatives-instagram-uniform-across-adset",
      "If any creative uses an Instagram account, every creative in this ad set must choose one (each can use a different account).",
      (creatives) => {
        if (!Array.isArray(creatives) || creatives.length === 0) return true;
        const hasInstagram = (row: unknown) =>
          String(
            (row as { instagramAccount?: { id?: string } | null })?.instagramAccount
              ?.id ?? ""
          ).trim() !== "";
        if (!creatives.some(hasInstagram)) return true;
        return creatives.every(hasInstagram);
      }
    )
    .required(),
  smart_targeting: y.boolean().required(),
  custom_audiences: y
    .array()
    .of(y.mixed<AudienceData>().required())
    .test(
      "manual-targeting-signal-when-visible",
      "When Smart Targeting is disabled, add at least one interest, location, or custom audience",
      function (value) {
        const context = this.options.context as
          | FlowValidationContext
          | undefined;
        if (!shouldRequireAudienceSection(context)) return true;
        if (this.parent.smart_targeting) return true;
        const parent = this.parent as {
          interests?: { key?: string }[];
          countries?: unknown[];
          geo_markets?: unknown[];
          regions?: unknown[];
          cities?: unknown[];
          age_min?: number | null;
          age_max?: number | null;
          genders?: string | null;
        };
        return hasManualAudienceTargetingSignal({
          custom_audiences: value,
          interests: parent.interests,
          countries: parent.countries,
          geo_markets: parent.geo_markets,
          regions: parent.regions,
          cities: parent.cities,
          age_min: parent.age_min,
          age_max: parent.age_max,
          genders: parent.genders,
        });
      }
    ),
  audience_search_query: y.string().default(""),
  // Targeting fields (used when smart_targeting is false) — locations, age/gender, interests
  countries: y
    .array()
    .of(
      y.object({
        key: y.string().nullable(),
        value: y.string().nullable(),
      })
    )
    .default([]),
  geo_markets: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        name: y.string(),
        country: y.string(),
        value: y.string().required(),
      })
    )
    .default([]),
  regions: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  cities: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  age_min: y.number().nullable().default(18),
  age_max: y.number().nullable().default(65),
  genders: y.string().nullable().default("all"),
  interests: y
    .array()
    .of(
      y.object({
        key: y.string().required(),
        value: y.string().required(),
      })
    )
    .default([]),
  targeting_query: y.string().default(""),
  targeting_subtype: y
    .object({
      value: y.string(),
      label: y.string(),
    })
    .nullable()
    .default(null),
  budget: y.object({
    type: y
      .string()
      .oneOf(["daily", "lifetime"])
      .test(
        "budget-type-required-when-visible",
        "Budget type is required",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          return Boolean(value);
        }
      ),
    amount: y.object({
      amount: y
        .number()
        .nullable()
        .typeError("Budget amount must be a valid number")
        .test(
          "budget-amount-required-when-visible",
          "Budget amount must be greater than 0",
          function (value) {
            const context = this.options.context as
              | FlowValidationContext
              | undefined;
            if (!shouldRequireBudgetSection(context)) return true;
            return (value ?? 0) > 0;
          }
        ),
      currency: y.string().required(),
    }),
  }),
  schedule: y.object({
    start_at: y
      .string()
      .test(
        "schedule-start-required-when-visible",
        "Start date is required",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          return Boolean(value);
        }
      ),
    end_at: y
      .string()
      .test(
        "schedule-end-required-when-visible",
        "End date is required",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          return Boolean(value);
        }
      )
      .test(
        "end-after-start-when-visible",
        "End date must be after start date",
        function (value) {
          const context = this.options.context as
            | FlowValidationContext
            | undefined;
          if (!shouldRequireBudgetSection(context)) return true;
          const startAt = this.parent.start_at;
          if (!startAt || !value) return false;
          const tz =
            context?.eventTimezone?.trim() || GUIDED_EDIT_DEFAULT_TIMEZONE;
          const start = dayjs.tz(startAt.slice(0, 16), tz);
          const end = dayjs.tz(value.slice(0, 16), tz);
          return start.isValid() && end.isValid() && end.isAfter(start);
        }
      ),
  }),
});

export type CreateCampaignFlowValues = y.InferType<
  typeof CreateCampaignFlowSchema
>;
